<template>
  <div class="form-page">
    <div class="form-page__header">
      <h2 class="form-page__title">
        {{ sectionTitle(modelValue.document_type) }}
        <span class="badge badge--blue" style="margin-left: 10px; vertical-align: middle;">✦ AutoFill</span>
      
      </h2>
      <p class="form-page__subtitle">
        FormIQ 3 hat aus Ihrem hochgeladenen Dokument folgende Angaben automatisch erkannt. Bitte
        prüfen Sie die Werte und korrigieren Sie diese bei Bedarf.
      </p>
    </div>

    <p v-if="!fields.length" class="empty-hint">
      Für dieses Dokument wurden keine weiteren Angaben ausgelesen.
    </p>

    <div v-else class="form-section">
      <div class="field-group">
        <div v-for="field in fields" :key="field.key" class="field">
          <label class="field__label">{{ field.label }}</label>

          <BaumlisteEditor
            v-if="field.type === 'baeume'"
            :model-value="modelValue.extraction?.[field.key] || []"
            @update:model-value="updateField(field.key, $event)"
          />

          <textarea
            v-else-if="field.type === 'textarea'"
            class="field__textarea"
            :class="{ 'autofill-highlight': hasValue(field.key) }"
            :maxlength="field.maxlength"
            :value="modelValue.extraction?.[field.key]"
            @input="updateField(field.key, $event.target.value)"
          />

          <select
            v-else-if="field.type === 'select'"
            class="field__select"
            :class="{ 'autofill-highlight': hasValue(field.key) }"
            :value="modelValue.extraction?.[field.key]"
            @change="updateField(field.key, $event.target.value)"
          >
            <option value="">Bitte wählen</option>
            <option v-for="option in field.options" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>

          <input
            v-else
            :type="field.type === 'date' ? 'date' : 'text'"
            class="field__input"
            :class="{ 'autofill-highlight': hasValue(field.key) }"
            :value="modelValue.extraction?.[field.key]"
            @input="updateField(field.key, $event.target.value)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import BaumlisteEditor from '../../components/BaumlisteEditor.vue';

const SECTION_TITLES = {
  VOLLMACHT: 'Vollmacht',
  LAGEPLAN: 'Lageplan',
  VORHABENSBESCHREIBUNG: 'Vorhabensbeschreibung',
  NACHWEIS_GEBUEHRENBEFREIUNG: 'Nachweis Gebührenbefreiung',
  MISC: 'Sonstiges Dokument',
};

// Exported so ApplicationViewC can label one wizard step per category
// without duplicating this lookup.
export const sectionTitle = (documentType) => SECTION_TITLES[(documentType || '').toUpperCase()] || documentType;

const GEBUEHREN_GRUND_OPTIONS = [
  { value: '1', label: '1 – Behörden und nichtrechtsfähige Anstalten des Bundes, der Länder, der Gemeinden und der Gemeindeverbände' },
  { value: '2', label: '2 – Körperschaften, Anstalten und Stiftungen des öffentlichen Rechts' },
  { value: '3', label: '3 – Kirchen, Religionsgemeinschaften und Weltanschauungsgemeinschaften' },
  { value: '4', label: '4 – gemeinnützige, mildtätige oder kirchliche Einrichtungen' },
];

// Meta fields shown as read-only badges in the section heading, never as
// editable inputs (see the <h3> above).
const META_KEYS = ['predicted_document_type', 'predicted_document_type_certainity', 'validity_score', 'completeness'];

// True if this document type's extraction has anything left to review once
// metadata is stripped - false for schema-only categories like MISC, which
// have no extractable fields at all. Used by ApplicationViewC to skip the
// Antragsdetails wizard step for those categories entirely.
export const hasReviewableFields = (extraction) => Object.keys(extraction || {}).some((key) => !META_KEYS.includes(key));

// Nice labels/input hints for known fields across all document_extraction
// schemas (categorise/document_categorise_workflow_*.json). Any field the
// model returns that isn't listed here still gets rendered - as a plain text
// input with a prettified label - so every extracted value gets an input,
// not just the ones anticipated here.
const FIELD_LABELS = {
  organisationsname: 'Organisationsname',
  authorised_party: 'Bevollmächtigte Partei',
  scope_of_authority: 'Umfang der Vollmacht',
  signatory_name: 'Name der unterschreibenden Person',
  signatory_role: 'Rolle der unterschreibenden Person',
  issue_date: 'Ausstellungsdatum',
  issue_location: 'Ausstellungsort',
  gps_rechts: 'GPS Rechtswert',
  gps_hoch: 'GPS Hochwert',
  scale: 'Maßstab',
  plan_source: 'Quelle des Plans',
  plan_date: 'Datum des Plans',
  baeume: 'Baumliste',
  kurzbezeichnung: 'Kurzbezeichnung der Maßnahme',
  begruendung: 'Begründung',
  strasse_uebern: 'Straße',
  postleitzahl_uebern: 'Postleitzahl',
  ort_uebern: 'Ort',
  telefon: 'Telefon',
  email_juristisch: 'E-Mail',
  issuing_authority: 'Ausstellende Behörde',
  reference_number: 'Aktenzeichen / Steuernummer',
  recipient_name: 'Empfänger',
  legal_basis: 'Rechtsgrundlage',
  grund: 'Grund der Gebührenbefreiung',
};

const TEXTAREA_KEYS = ['scope_of_authority', 'begruendung'];
const DATE_KEYS = ['issue_date', 'plan_date'];
const MAXLENGTH = { kurzbezeichnung: 70, begruendung: 500, scope_of_authority: 500 };
const SELECT_OPTIONS = { grund: GEBUEHREN_GRUND_OPTIONS };

const prettifyKey = (key) => key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');

// Fields are derived from whatever keys the extraction actually contains,
// not a fixed per-document-type whitelist - so every value FormIQ 3 returns
// gets an input, even for fields not anticipated above. Exported so the
// summary step can list the same fields/labels without duplicating them.
export const fieldsFromExtraction = (extraction) => Object.keys(extraction || {})
  .filter((key) => !META_KEYS.includes(key))
  .map((key) => {
    const value = extraction[key];
    const label = FIELD_LABELS[key] || prettifyKey(key);
    if (Array.isArray(value)) return { key, label, type: 'baeume' };
    if (SELECT_OPTIONS[key]) return { key, label, type: 'select', options: SELECT_OPTIONS[key] };
    if (TEXTAREA_KEYS.includes(key)) return { key, label, type: 'textarea', maxlength: MAXLENGTH[key] };
    if (DATE_KEYS.includes(key)) return { key, label, type: 'date' };
    return { key, label, type: 'text', maxlength: MAXLENGTH[key] };
  });

export default {
  name: 'BaumAntragsdetailsStep',
  components: { BaumlisteEditor },
  props: {
    modelValue: { type: Object, default: () => ({ document_type: '', extraction: {} }) },
    showErrors: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const fields = computed(() => fieldsFromExtraction(props.modelValue.extraction));

    const hasValue = (key) => {
      const value = props.modelValue.extraction?.[key];
      return Array.isArray(value) ? value.length > 0 : !!value;
    };

    const updateField = (key, value) => {
      emit('update:modelValue', {
        ...props.modelValue,
        extraction: { ...props.modelValue.extraction, [key]: value },
      });
    };

    return { fields, sectionTitle, hasValue, updateField };
  },
};
</script>

<style scoped src="../form.css"></style>
<style scoped>
.empty-hint { font-size: 13px; color: #9ca3af; }

.badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 20px;
  margin-left: 8px;
  vertical-align: middle;
}
.badge--blue { background: #eef4fb; color: #1d4ed8; border: 1px solid #c3d9f0; }
.badge--score { background: #f0fdf4; color: #065f46; border: 1px solid #bbf7d0; }
.badge--certainty { background: #fef9e7; color: #7a4e00; border: 1px solid #f5d87e; }
</style>
